import { Mutex } from 'async-mutex';

import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import env from '@config/env';

import { User } from '@interfaces/users.interface';

import { logout, setCredentials } from '@slices/auth.slice';

export function isApiError(
  error: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): error is FetchBaseQueryError & { data: any } {
  return typeof error === 'object' && error != null && 'status' in error;
}

const BASE_URL = env.API_URL;
const LOGIN_PATH = env.API_LOGIN_PATH;
const REFRESH_PATH = env.API_REFRESH_PATH;

// Create a new mutex
const mutex = new Mutex();

// Use this variable to prevent multiple refresh if the first one fails
let hasRefreshFailed = false;

const baseQueryFn = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include',
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();

    for (const key in params) {
      const value = params[key];
      if (Array.isArray(value)) {
        // If the value is an array, append each item to the search params
        value.forEach((item) => {
          searchParams.append(key, item);
        });
      } else {
        searchParams.append(key, params[key]);
      }
    }

    return searchParams.toString();
  },
});

const baseQuery: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // Reset refresh failed flag
  hasRefreshFailed = false;

  // Wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  let result = await baseQueryFn(args, api, extraOptions);

  if (
    !hasRefreshFailed &&
    args.url !== LOGIN_PATH &&
    args.url !== REFRESH_PATH &&
    result.error?.status === 401
  ) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Try refresh token
        const refreshResult = await baseQueryFn(
          { url: REFRESH_PATH, method: 'POST' },
          api,
          extraOptions,
        );

        if (refreshResult.error) {
          api.dispatch(logout());

          // Release mutex
          hasRefreshFailed = true;
          release();

          return result;
        } else {
          const user = refreshResult.data as User;

          api.dispatch(setCredentials(user));

          // Retry the initial query
          result = await baseQueryFn(args, api, extraOptions);
        }
      } finally {
        // Release  the mutex
        release();
      }
    } else {
      // Wait until the mutex is available without locking it
      await mutex.waitForUnlock();

      // Don't retry if the refresh failed
      if (!hasRefreshFailed) {
        result = await baseQueryFn(args, api, extraOptions);
      }
    }
  }

  return result;
};

const api = createApi({
  baseQuery,
  tagTypes: [
    'users',
    'customers',
    'cart',
    'customer',
    'addresses',
    'templates',
    'search-analytics',
  ], // Define here tags for cache invalidation
  endpoints: () => ({}),
});

export default api;
